import TitleBar from '../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../../../components/common/Footer';
import { Button } from '../../../components';
import { FETCH_DIVISIONS_INVENTORY } from '../queries';
import { useQuery } from '@apollo/client';
import FinancialData from '../../../components/common/FinancialData';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import TitleUpdater from '../../../components/common/TitleUpdater';

type Inventory = {
  monthsOnHand: number;
  revenueEquivalent: number;
  key: string;
};

type InventoryDashboard = {
  inventories: Inventory[];
  isDefault: boolean;
  name: string;
  key: string;
};

interface DataReturned {
  inventoryDashboard: InventoryDashboard[];
}

type InventoryTransformedData = Omit<InventoryDashboard, 'inventories'> & {
  revenueEquivalent: number;
  monthsOnHand: number;
};

function FinishedGoodsInventory() {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_DIVISIONS_INVENTORY,
  );

  const { selectedTags, isDivision } = useUserContext();

  const divisionsData = data?.inventoryDashboard || [];

  const filteredDivisions = getFilteredTags({
    data: divisionsData,
    selectedTags,
    condition: isDivision,
    lookUpKey: 'key',
  });

  const convertReturnedData = (
    data: DataReturned,
  ): InventoryTransformedData[] => {
    return data?.inventoryDashboard?.map((inventory) => {
      return {
        revenueEquivalent: inventory.inventories[0]?.revenueEquivalent!,
        monthsOnHand: inventory.inventories[0]?.monthsOnHand!,
        isDefault: inventory.isDefault,
        name: inventory.name,
        key: inventory.key,
      };
    });
  };

  const columns: ColumnsType<InventoryTransformedData> = [
    {
      title: 'Division',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Finished Goods Inventory',
      dataIndex: 'monthsOnHand',
      key: 'monthsOnHand',
      align: 'right',
      width: 200,
      sorter: (a, b) =>
        tableSortingMethods.number(a.monthsOnHand, b.monthsOnHand),
      render: (text: number) => (
        <span className="custom-table--column">{text || 0} mths</span>
      ),
    },
    {
      title: 'Revenue Equivalent',
      dataIndex: 'revenueEquivalent',
      key: 'revenueEquivalent',
      align: 'right',
      width: 200,
      sorter: (a, b) =>
        tableSortingMethods.number(a.revenueEquivalent, b.revenueEquivalent),
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
  ];

  const extendActions = (record: any) => {
    return (
      <Button
        type="primary-rounded"
        className="text-sm min-w-[140px]"
        onClick={() =>
          navigate(`/finished-goods-inventory/${record.key}/division-detail`)
        }
      >
        Edit Inventory
      </Button>
    );
  };

  const baseColumns: ExportColumn[] = [
    {
      key: 'name',
      header: 'Division',
      type: 'text',
      width: 45,
    },
    {
      key: 'monthsOnHand',
      header: 'Finished Goods Inventory',
      type: 'number',
      width: 35,
    },
    {
      key: 'revenueEquivalent',
      header: 'Revenue Equivalent',
      type: 'money',
    },
  ];

  return (
    <>
      <TitleUpdater title={`Finished Goods Inventory`} />
      <TitleBar
        title="Finished Goods Inventory"
        subtitle="Overview"
        brief="Finished goods are your first line of defense when it comes to mitigation. This inventory
                      can buy you valuable time while mitigation strategies are ramped up to deal with a business
                      disruption."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Finished Goods Inventory"
          columns={columns}
          data={convertReturnedData({ inventoryDashboard: filteredDivisions })}
          loading={loading}
          extendActions={extendActions}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/exposures'} />
    </>
  );
}

export default FinishedGoodsInventory;
